import { Note } from "@/types/note";
import { getNotes } from "./storage";

export interface TopicGroup {
  topic: string;
  notes: Note[];
  summary: string;
}

export interface OccasionGroup {
  occasion: string;
  topics: TopicGroup[];
}

export interface CategoryGroup {
  category: string;
  occasions: OccasionGroup[];
}

export interface DaySummary {
  date: Date;
  dateString: string;
  categories: CategoryGroup[];
  totalNotes: number;
}

/**
 * Get notes for a specific date
 */
export function getNotesForDate(date: Date): Note[] {
  const notes = getNotes();
  return notes.filter((note) => {
    const noteDate = new Date(note.createdAt);
    return (
      noteDate.getFullYear() === date.getFullYear() &&
      noteDate.getMonth() === date.getMonth() &&
      noteDate.getDate() === date.getDate()
    );
  });
}

/**
 * Get dates with notes in the last N days
 */
export function getDatesWithNotes(days: number = 7): Date[] {
  const notes = getNotes();
  const dateSet = new Set<string>();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  notes.forEach((note) => {
    const noteDate = new Date(note.createdAt);
    noteDate.setHours(0, 0, 0, 0);

    const diffTime = today.getTime() - noteDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays >= 0 && diffDays < days) {
      dateSet.add(noteDate.toISOString().split("T")[0]);
    }
  });

  return Array.from(dateSet)
    .map((d) => new Date(d))
    .sort((a, b) => b.getTime() - a.getTime());
}

/**
 * Simple offline text summarization using extractive summarization
 * Extracts key phrases and common words from notes
 */
function summarizeNotes(notes: Note[]): string {
  if (notes.length === 0) return "";
  if (notes.length === 1) {
    return notes[0].content.trim();
  }

  // Combine all note contents
  const allContent = notes.map((n) => n.content).join(" ");

  // Simple extractive summarization:
  // 1. Split into sentences
  const sentences = allContent
    .split(/[.!?]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 10);

  if (sentences.length === 0) {
    return allContent;
  }

  // 2. Find word frequencies (excluding common stop words)
  const stopWords = new Set([
    "the",
    "a",
    "an",
    "is",
    "are",
    "was",
    "were",
    "be",
    "been",
    "being",
    "have",
    "has",
    "had",
    "do",
    "does",
    "did",
    "will",
    "would",
    "could",
    "should",
    "may",
    "might",
    "must",
    "shall",
    "can",
    "need",
    "dare",
    "ought",
    "used",
    "to",
    "of",
    "in",
    "for",
    "on",
    "with",
    "at",
    "by",
    "from",
    "as",
    "into",
    "through",
    "during",
    "before",
    "after",
    "above",
    "below",
    "between",
    "under",
    "again",
    "further",
    "then",
    "once",
    "here",
    "there",
    "when",
    "where",
    "why",
    "how",
    "all",
    "each",
    "few",
    "more",
    "most",
    "other",
    "some",
    "such",
    "no",
    "nor",
    "not",
    "only",
    "own",
    "same",
    "so",
    "than",
    "too",
    "very",
    "just",
    "and",
    "but",
    "if",
    "or",
    "because",
    "until",
    "while",
    "this",
    "that",
    "these",
    "those",
    "i",
    "me",
    "my",
    "we",
    "our",
    "you",
    "your",
    "it",
    "its",
    "they",
    "them",
    "their",
  ]);

  const wordFreq: Record<string, number> = {};
  const words = allContent.toLowerCase().split(/\s+/);

  words.forEach((word) => {
    const cleanWord = word.replace(/[^a-z0-9]/g, "");
    if (cleanWord.length > 2 && !stopWords.has(cleanWord)) {
      wordFreq[cleanWord] = (wordFreq[cleanWord] || 0) + 1;
    }
  });

  // 3. Score sentences based on word frequency
  const scoredSentences = sentences.map((sentence) => {
    const sentenceWords = sentence.toLowerCase().split(/\s+/);
    let score = 0;
    sentenceWords.forEach((word) => {
      const cleanWord = word.replace(/[^a-z0-9]/g, "");
      score += wordFreq[cleanWord] || 0;
    });
    return { sentence, score: score / sentenceWords.length };
  });

  // 4. Get top 2 sentences
  scoredSentences.sort((a, b) => b.score - a.score);
  const topSentences = scoredSentences.slice(0, 2).map((s) => s.sentence);

  return topSentences.join(". ");
}

/**
 * Build day summary with hierarchical grouping
 */
export function buildDaySummary(date: Date): DaySummary {
  const notes = getNotesForDate(date);

  // Sort notes by creation time
  notes.sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  // Group by category -> occasion -> topic
  const categoryMap = new Map<string, Map<string, Map<string, Note[]>>>();

  notes.forEach((note) => {
    const category = note.category || "Uncategorized";
    const occasion = note.occasion || "General";
    const topic = note.topic || "Untitled";

    if (!categoryMap.has(category)) {
      categoryMap.set(category, new Map());
    }
    const occasionMap = categoryMap.get(category)!;

    if (!occasionMap.has(occasion)) {
      occasionMap.set(occasion, new Map());
    }
    const topicMap = occasionMap.get(occasion)!;

    if (!topicMap.has(topic)) {
      topicMap.set(topic, []);
    }
    topicMap.get(topic)!.push(note);
  });

  // Convert to structured format
  const categories: CategoryGroup[] = [];

  categoryMap.forEach((occasionMap, category) => {
    const occasions: OccasionGroup[] = [];

    occasionMap.forEach((topicMap, occasion) => {
      const topics: TopicGroup[] = [];

      topicMap.forEach((topicNotes, topic) => {
        topics.push({
          topic,
          notes: topicNotes,
          summary: summarizeNotes(topicNotes),
        });
      });

      occasions.push({ occasion, topics });
    });

    categories.push({ category, occasions });
  });

  return {
    date,
    dateString: formatDateString(date),
    categories,
    totalNotes: notes.length,
  };
}

function formatDateString(date: Date): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const targetDate = new Date(date);
  targetDate.setHours(0, 0, 0, 0);

  const diffTime = today.getTime() - targetDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    return days[date.getDay()];
  }

  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

// ==================== Weekly/Date Range Summary ====================

export interface WeeklyTopicGroup {
  topic: string;
  notes: Note[];
  summary: string;
  dateRange: { start: Date; end: Date };
}

export interface WeeklyOccasionGroup {
  occasion: string;
  topics: WeeklyTopicGroup[];
}

export interface WeeklyCategoryGroup {
  category: string;
  occasions: WeeklyOccasionGroup[];
  dateRange: { start: Date; end: Date };
}

export interface WeeklySummary {
  categories: WeeklyCategoryGroup[];
  totalNotes: number;
  dateRange: { start: Date; end: Date };
}

/**
 * Get notes within a date range
 */
export function getNotesInRange(startDate: Date, endDate: Date): Note[] {
  const notes = getNotes();
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(endDate);
  end.setHours(23, 59, 59, 999);

  return notes.filter((note) => {
    const noteDate = new Date(note.createdAt);
    return noteDate >= start && noteDate <= end;
  });
}

/**
 * Get default date range (last 7 days)
 */
export function getDefaultDateRange(): { start: Date; end: Date } {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 6);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

/**
 * Build weekly summary grouped by Category -> Occasion -> Topic
 * with date range filtering per group
 */
export function buildWeeklySummary(
  startDate?: Date,
  endDate?: Date,
): WeeklySummary {
  const range =
    startDate && endDate
      ? { start: startDate, end: endDate }
      : getDefaultDateRange();

  const notes = getNotesInRange(range.start, range.end);

  // Sort notes by creation time (chronological)
  notes.sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  // Group by category -> occasion -> topic
  const categoryMap = new Map<
    string,
    { notes: Note[]; occasionMap: Map<string, Map<string, Note[]>> }
  >();

  notes.forEach((note) => {
    const category = note.category || "Uncategorized";
    const occasion = note.occasion || "General";
    const topic = note.topic || "Untitled";

    if (!categoryMap.has(category)) {
      categoryMap.set(category, { notes: [], occasionMap: new Map() });
    }
    const catData = categoryMap.get(category)!;
    catData.notes.push(note);

    if (!catData.occasionMap.has(occasion)) {
      catData.occasionMap.set(occasion, new Map());
    }
    const topicMap = catData.occasionMap.get(occasion)!;

    if (!topicMap.has(topic)) {
      topicMap.set(topic, []);
    }
    topicMap.get(topic)!.push(note);
  });

  // Convert to structured format
  const categories: WeeklyCategoryGroup[] = [];

  categoryMap.forEach((catData, category) => {
    const occasions: WeeklyOccasionGroup[] = [];

    // Calculate date range for this category
    const catNotes = catData.notes;
    const catStart = new Date(
      Math.min(...catNotes.map((n) => new Date(n.createdAt).getTime())),
    );
    const catEnd = new Date(
      Math.max(...catNotes.map((n) => new Date(n.createdAt).getTime())),
    );

    catData.occasionMap.forEach((topicMap, occasion) => {
      const topics: WeeklyTopicGroup[] = [];

      topicMap.forEach((topicNotes, topic) => {
        const topicStart = new Date(
          Math.min(...topicNotes.map((n) => new Date(n.createdAt).getTime())),
        );
        const topicEnd = new Date(
          Math.max(...topicNotes.map((n) => new Date(n.createdAt).getTime())),
        );

        topics.push({
          topic,
          notes: topicNotes,
          summary: summarizeNotesPublic(topicNotes),
          dateRange: { start: topicStart, end: topicEnd },
        });
      });

      occasions.push({ occasion, topics });
    });

    categories.push({
      category,
      occasions,
      dateRange: { start: catStart, end: catEnd },
    });
  });

  return {
    categories,
    totalNotes: notes.length,
    dateRange: range,
  };
}

/**
 * Build summary for a specific category with custom date range
 */
export function buildCategorySummary(
  category: string,
  startDate: Date,
  endDate: Date,
): WeeklyCategoryGroup | null {
  const notes = getNotesInRange(startDate, endDate).filter(
    (n) => (n.category || "Uncategorized") === category,
  );

  if (notes.length === 0) return null;

  notes.sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );

  const occasionMap = new Map<string, Map<string, Note[]>>();

  notes.forEach((note) => {
    const occasion = note.occasion || "General";
    const topic = note.topic || "Untitled";

    if (!occasionMap.has(occasion)) {
      occasionMap.set(occasion, new Map());
    }
    const topicMap = occasionMap.get(occasion)!;

    if (!topicMap.has(topic)) {
      topicMap.set(topic, []);
    }
    topicMap.get(topic)!.push(note);
  });

  const occasions: WeeklyOccasionGroup[] = [];

  occasionMap.forEach((topicMap, occasion) => {
    const topics: WeeklyTopicGroup[] = [];

    topicMap.forEach((topicNotes, topic) => {
      const topicStart = new Date(
        Math.min(...topicNotes.map((n) => new Date(n.createdAt).getTime())),
      );
      const topicEnd = new Date(
        Math.max(...topicNotes.map((n) => new Date(n.createdAt).getTime())),
      );

      topics.push({
        topic,
        notes: topicNotes,
        summary: summarizeNotesPublic(topicNotes),
        dateRange: { start: topicStart, end: topicEnd },
      });
    });

    occasions.push({ occasion, topics });
  });

  const catStart = new Date(
    Math.min(...notes.map((n) => new Date(n.createdAt).getTime())),
  );
  const catEnd = new Date(
    Math.max(...notes.map((n) => new Date(n.createdAt).getTime())),
  );

  return {
    category,
    occasions,
    dateRange: { start: catStart, end: catEnd },
  };
}

/**
 * Public version of summarizeNotes for use in weekly summary
 */
export function summarizeNotesPublic(notes: Note[]): string {
  if (notes.length === 0) return "";
  if (notes.length === 1) {
    return notes[0].content.trim();
  }

  // Combine all note contents
  const allContent = notes.map((n) => n.content).join(" ");

  // Simple extractive summarization
  const sentences = allContent
    .split(/[.!?]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 10);

  if (sentences.length === 0) {
    return allContent;
  }

  const stopWords = new Set([
    "the",
    "a",
    "an",
    "is",
    "are",
    "was",
    "were",
    "be",
    "been",
    "being",
    "have",
    "has",
    "had",
    "do",
    "does",
    "did",
    "will",
    "would",
    "could",
    "should",
    "may",
    "might",
    "must",
    "shall",
    "can",
    "need",
    "dare",
    "ought",
    "used",
    "to",
    "of",
    "in",
    "for",
    "on",
    "with",
    "at",
    "by",
    "from",
    "as",
    "into",
    "through",
    "during",
    "before",
    "after",
    "above",
    "below",
    "between",
    "under",
    "again",
    "further",
    "then",
    "once",
    "here",
    "there",
    "when",
    "where",
    "why",
    "how",
    "all",
    "each",
    "few",
    "more",
    "most",
    "other",
    "some",
    "such",
    "no",
    "nor",
    "not",
    "only",
    "own",
    "same",
    "so",
    "than",
    "too",
    "very",
    "just",
    "and",
    "but",
    "if",
    "or",
    "because",
    "until",
    "while",
    "this",
    "that",
    "these",
    "those",
    "i",
    "me",
    "my",
    "we",
    "our",
    "you",
    "your",
    "it",
    "its",
    "they",
    "them",
    "their",
  ]);

  const wordFreq: Record<string, number> = {};
  const words = allContent.toLowerCase().split(/\s+/);

  words.forEach((word) => {
    const cleanWord = word.replace(/[^a-z0-9]/g, "");
    if (cleanWord.length > 2 && !stopWords.has(cleanWord)) {
      wordFreq[cleanWord] = (wordFreq[cleanWord] || 0) + 1;
    }
  });

  const scoredSentences = sentences.map((sentence) => {
    const sentenceWords = sentence.toLowerCase().split(/\s+/);
    let score = 0;
    sentenceWords.forEach((word) => {
      const cleanWord = word.replace(/[^a-z0-9]/g, "");
      score += wordFreq[cleanWord] || 0;
    });
    return { sentence, score: score / sentenceWords.length };
  });

  scoredSentences.sort((a, b) => b.score - a.score);
  const topSentences = scoredSentences.slice(0, 2).map((s) => s.sentence);

  return topSentences.join(". ");
}
